<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useEquipmentFactorStore } from '@/stores/equipmentFactor'
import type { EquipmentFactorDto, TransportLeaf, TransportNode } from '@/stores/equipmentFactor'
import { TextField } from '@haskoning/vue-component-library'
import type { ActivityDto, MaterialDto } from '@/stores/buildingBlock'
import { useActivityStore } from '@/stores/activity'
import { useBuildingBlockStatus } from '@/components/building-block-form/composable/useBuildingBlockStatus'
import { handleWheelOnNumericField } from '@/utils/handleWheelOnNumericField'
import { useMaterialImpact } from '@/components/building-block-form/utils/useMaterialImpact'

const activityStore = useActivityStore()
const { applyActivityImpact } = useMaterialImpact()

const infoMenu = ref(false)
const props = defineProps<{
  activity: ActivityDto
  material: MaterialDto
  default_value: number
  readonly?: boolean
}>()
const emit = defineEmits<{
  update: [value?: number | null]
}>()
const { run } = useBuildingBlockStatus()

const equipmentFactorStore = useEquipmentFactorStore()

const currentEquipmentFactor = ref<Partial<EquipmentFactorDto>>()

const onValueTyping = (event: Event) => {
  if (props.readonly) return
  const inputElement = event.target instanceof HTMLInputElement ? event.target : null
  const textValue = inputElement?.value?.trim() ?? ''
  if (textValue === '') {
    emit('update', null)
  } else {
    const parsedValue = Number(textValue)
    if (!Number.isNaN(parsedValue)) {
      emit('update', parsedValue)
    }
  }
}

onMounted(async () => {
  // Ensure equipment factors are loaded before initializing selection
  if (!equipmentFactorStore.items || equipmentFactorStore.items.length === 0) {
    await equipmentFactorStore.fetchItems?.()
  }
  const matchingEquipmentFactor = equipmentFactorStore.items.find(
    (equipmentFactor) => equipmentFactor.id === props.activity.equipment_factor
  )
  if (matchingEquipmentFactor) currentEquipmentFactor.value = { ...matchingEquipmentFactor }
  if (
    matchingEquipmentFactor &&
    matchingEquipmentFactor.energy_carrier_type &&
    currentEquipmentFactor.value
  ) {
    currentEquipmentFactor.value.energy_carrier_type = matchingEquipmentFactor.energy_carrier_type
  }

  // If no match from persisted selection, initialize to first category
  if (!currentEquipmentFactor.value) {
    currentEquipmentFactor.value = {
      category: '',
      equipment_spec: '',
      energy_carrier_type: '',
      unit: ''
    }
  }
})

async function onCatergoriesChange(value: string) {
  if (props.readonly) return
  if (!currentEquipmentFactor.value) return

  currentEquipmentFactor.value.category = value
  currentEquipmentFactor.value.equipment_spec = ''
  currentEquipmentFactor.value.energy_carrier_type = ''
  const updatedActivity = await run(
    () =>
      activityStore.update({
        ...props.activity,
        equipment_factor: undefined
      }),
    props.material.block
  )
  applyActivityImpact(updatedActivity)
}

async function onSpecChange(value: string) {
  if (props.readonly) return
  if (!currentEquipmentFactor.value) return
  currentEquipmentFactor.value.equipment_spec = value
  currentEquipmentFactor.value.energy_carrier_type = ''
  const updatedActivity = await run(
    () =>
      activityStore.update({
        ...props.activity,
        equipment_factor: undefined
      }),
    props.material.block
  )
  applyActivityImpact(updatedActivity)
}

async function onCarrierChange(value: string) {
  if (props.readonly) return
  if (!currentEquipmentFactor.value) return

  const matched = equipmentFactorStore.items.find(
    (equipmentFactor) =>
      equipmentFactor.energy_carrier_type === value &&
      equipmentFactor.equipment_spec === currentEquipmentFactor.value?.equipment_spec &&
      equipmentFactor.category === currentEquipmentFactor.value?.category
  )
  currentEquipmentFactor.value = matched ? { ...matched } : currentEquipmentFactor.value

  // Calculate value based on default_value / mki_phase_AD
  let calculatedValue = props.activity.value
  const mkiPhaseAD = Number(currentEquipmentFactor.value?.mki_phase_AD)
  if (!Number.isNaN(mkiPhaseAD) && mkiPhaseAD !== 0) {
    calculatedValue = props.default_value / mkiPhaseAD
  }

  // Persist selection to activity and save
  if (currentEquipmentFactor.value?.id) {
    const equipmentFactorId = currentEquipmentFactor.value.id
    const updatedActivity = await run(
      () =>
        activityStore.update({
          ...props.activity,
          equipment_factor: equipmentFactorId,
          value: calculatedValue
        }),
      props.material.block
    )
    applyActivityImpact(updatedActivity)
  }
}

function isSpecNode(node: TransportNode): node is {
  category: string
  values: Array<{ equipment_spec: string; values: TransportLeaf[] }>
} {
  const firstValue = node.values[0]
  return (
    Array.isArray(node.values) &&
    node.values.length > 0 &&
    typeof firstValue === 'object' &&
    firstValue !== null &&
    'equipment_spec' in firstValue
  )
}

function hasSpecLayerForCurrentCategory(): boolean {
  const node = equipmentFactorStore.transportTree.find(
    (transportNode) => transportNode.category === currentEquipmentFactor.value?.category
  )
  if (!node) return false
  return isSpecNode(node)
}

function getSpecOptionsForCurrentCategory(): string[] {
  const node = equipmentFactorStore.transportTree.find(
    (transportNode) => transportNode.category === currentEquipmentFactor.value?.category
  )
  if (!node || !isSpecNode(node)) return []
  // Map spec layer to spec labels
  return node.values
    .map((specOption) => specOption.equipment_spec)
    .filter((equipmentSpec) => equipmentSpec && equipmentSpec !== '')
}

function getEnergyCarrierOptionsForSelection(): string[] {
  const node = equipmentFactorStore.transportTree.find(
    (transportNode) => transportNode.category === currentEquipmentFactor.value?.category
  )
  if (!node) return []
  // No spec layer: values are leaves
  if (!isSpecNode(node)) {
    return node.values.map((transportLeaf) => transportLeaf.energy_carrier_type)
  }
  // With spec layer: find selected spec node then map leaves
  const specNode = node.values.find(
    (specOption) => specOption.equipment_spec === currentEquipmentFactor.value?.equipment_spec
  )
  return specNode ? specNode.values.map((transportLeaf) => transportLeaf.energy_carrier_type) : []
}
</script>

<template>
  <v-row class="ma-0 align-center pb-6">
    <!-- category -->
    <v-col cols="12" sm="6" md="3" lg="3" class="pa-2">
      <v-select
        color="primary"
        :model-value="currentEquipmentFactor?.category"
        :items="equipmentFactorStore.transportTree.map((transportNode) => transportNode.category)"
        label="Category"
        :readonly="props.readonly"
        :disabled="props.readonly"
        @update:model-value="onCatergoriesChange"
        hide-details
      />
    </v-col>
    <!-- equipment_spec (only if category node has spec layer) -->
    <v-col v-if="hasSpecLayerForCurrentCategory()" cols="12" sm="6" md="2" lg="2" class="pa-2">
      <v-select
        color="primary"
        :model-value="currentEquipmentFactor?.equipment_spec"
        :items="getSpecOptionsForCurrentCategory()"
        label="Equipment Spec"
        :readonly="props.readonly"
        :disabled="props.readonly"
        @update:model-value="onSpecChange"
        hide-details
      />
    </v-col>
    <!-- energy_carrier_type -->
    <v-col cols="12" sm="6" md="2" lg="2" class="pa-2">
      <v-select
        color="primary"
        v-if="getEnergyCarrierOptionsForSelection().length > 0"
        :model-value="currentEquipmentFactor?.energy_carrier_type"
        :items="getEnergyCarrierOptionsForSelection()"
        label="Energy Carrier Type"
        :readonly="props.readonly"
        :disabled="props.readonly"
        @update:model-value="onCarrierChange"
        hide-details
      />
    </v-col>
    <v-spacer />
    <!-- Numeric input using the unit from the selected leaf -->
    <v-col cols="12" sm="4" md="2" lg="2" class="pa-2">
      <TextField
        v-if="activityStore.fields?.value"
        :label="currentEquipmentFactor?.unit"
        :field="activityStore.fields?.value"
        :model-value="props.activity"
        :no-help-text="true"
        :readonly="props.readonly"
        @wheel="handleWheelOnNumericField"
        @input="onValueTyping"
        hide-details
      />
    </v-col>
    <v-col cols="12" sm="2" md="1" lg="1" class="pa-2">
      <v-menu v-if="activityStore.fields?.value" v-model="infoMenu" location="bottom">
        <template #activator="{ props: slotProps }">
          <v-btn
            v-bind="slotProps"
            border="md primary opacity-75"
            icon="mdi-information-symbol"
            variant="outlined"
            size="medium"
            :disabled="props.readonly"
          />
        </template>

        <v-card style="max-width: 320px; padding: 12px" class="border-md">
          <div style="font-size: 0.95rem; color: var(--v-theme-on-surface-variant)">
            <p v-if="props.activity.stage !== 'A5'">
              Give the number of
              <span class="font-weight-bold">{{ currentEquipmentFactor?.unit }}</span> needed to
              transport one unit of the material of 1 km
            </p>
            <p v-else>
              Give the number of
              <span class="font-weight-bold">{{ currentEquipmentFactor?.unit }}</span> needed to
              displace one unit of the material
            </p>
          </div>
        </v-card>
      </v-menu>
    </v-col>
    <v-col cols="12" sm="6" md="2" lg="2" class="pa-2">
      <v-text-field
        :model-value="currentEquipmentFactor?.mki_phase_AD ?? 'Nan'"
        color="primary"
        label="MKI per unit of fuel"
        variant="outlined"
        readonly
        hide-details
      />
    </v-col>
  </v-row>
</template>
